import { useCallback, useMemo, useState, useEffect } from "react";
import { Collapse, Button, Drawer, Badge, Radio } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { FilterOutlined, CheckOutlined } from "@ant-design/icons";

import { AppState } from "store";
import { cancelIcon, chevronClose, chevronOpen } from "svgs";
import { SwitchButton } from "components/base";
import { CheckboxPanelList, RadioPanelList } from "./sub-components";
import {
  fetchFilterClientAdminUsers,
  onChangeIssuesFilters,
  onChangeRootIssuesFilters,
  onChangeTimeRangeIssuesFilters,
  setAssigneeFilter,
  setIssuesListType,
} from "store/actions";
import { getAllCheckedStatus, getIndeterminate } from "utils/helpers";
import { IssuesState } from "types";
import "styles/components/tab-filters/issues-filter.scss";

export default function IssuesFilter() {
  const location = useLocation();
  const dispatch = useDispatch();
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);
  const [listType, setListType] = useState<string>("current");

  const {
    issues: { filters },
    common: { userType = 1 },
  } = useSelector<AppState, AppState>((state) => state);

  const {
    original: {
      "Issue Status": originalIssueStatus = [],
      "Issue Type": originalIssueType = [],
      "Time Range": originalTimeRange = [],
      Assignee: originalAssignee = [],
    },
    updated: {
      "Issue Status": updatedIssueStatus = [],
      "Issue Type": updatedIssueType = [],
      "Time Range": updatedTimeRange = [],
      Assignee: updatedAssignee = [],
    },
  } = filters;

  const { "Time Range": timeRangeUpdated, ...restUpdated } = filters.updated;
  const { "Time Range": timeRangeOriginal, ...restOriginal } = filters.original;

  const isAllChecked = useMemo(() => {
    return getAllCheckedStatus(restUpdated, restOriginal);
  }, [restUpdated, restOriginal]);

  const isIndeterminate = useMemo(() => {
    return getIndeterminate(restUpdated, restOriginal);
  }, [restUpdated, restOriginal]);

  // Count active filters (excluding Time Range as it's always selected)
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    Object.entries(restUpdated).forEach(([key, filterArray]: [string, any[]]) => {
      count += filterArray.length;
    });
    return count;
  }, [restUpdated]);

  // Handle responsive
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
      if (window.innerWidth >= 992) {
        setMobileDrawerOpen(false);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onCheckboxListChange = useCallback(
    (name, value, type) => {
      dispatch(onChangeIssuesFilters(name, value, type));
    },
    [dispatch]
  );

  const onTimeRangeChange = useCallback(
    (value, range) => {
      dispatch(onChangeTimeRangeIssuesFilters(value, range));
    },
    [dispatch]
  );

  const onRootChange = useCallback(
    (event) => {
      dispatch(onChangeRootIssuesFilters(event.target.checked));
    },
    [dispatch]
  );

  const onClearAll = useCallback(() => {
    dispatch(onChangeRootIssuesFilters(false));
  }, [dispatch]);

  const originalAssigneeWithTag = useMemo(
    () =>
      originalAssignee.map((assignee) => ({
        ...assignee,
        render: <></>,
      })),
    [originalAssignee]
  );

  const onListTypeChange = useCallback(
    (value: string) => {
      setListType(value);
      dispatch(setIssuesListType(value as IssuesState["filters"]["listType"]));
    },
    [dispatch]
  );

  const handleOpenDrawer = useCallback(() => {
    setMobileDrawerOpen(true);
  }, []);

  const handleCloseDrawer = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  const handleApplyFilters = useCallback(() => {
    setMobileDrawerOpen(false);
  }, []);

  useEffect(() => {
    if (userType == 1 || userType == 2) {
      dispatch(setAssigneeFilter());
    } else {
      dispatch(fetchFilterClientAdminUsers());
    }
  }, [dispatch, userType]);

  useEffect(() => {
    dispatch(onChangeRootIssuesFilters(false));
  }, [location.pathname, dispatch]);


const ListTypeSwitch = () => (
  <div className="custom-list-type-switch">
    <Radio.Group
      value={listType}
      onChange={(e) => onListTypeChange(e.target.value)}
      buttonStyle="solid"
      className="list-type-radio-group"
      style={{
        display: 'flex',
        width: '100%',
      }}
    >
      <Radio.Button 
        value="current"
        style={{
          flex: 1,
          textAlign: 'center',
          borderColor: listType === 'current' ? '#F9C534' : '#abababff',
          backgroundColor: listType === 'current' ? '#F9C534' : '#fff',
          color: listType === 'current' ? '#000000' : '#000',
        }}
      >
        Current
      </Radio.Button>
      <Radio.Button 
        value="archive"
        style={{
          flex: 1,
          textAlign: 'center',
          borderColor: listType === 'archive' ? '#F9C534' : '#abababff',
          backgroundColor: listType === 'archive' ? '#F9C534' : '#fff',
          color: listType === 'archive' ? '#000000' : '#000',
        }}
      >
        Archived
      </Radio.Button>
    </Radio.Group>
  </div>
);

  // Filter content - reused in both desktop and mobile
  const filterContent = (
    <Collapse
      className="__common-collapse --issues"
      ghost
      expandIconPosition="right"
      expandIcon={({ isActive }) => (isActive ? chevronOpen : chevronClose)}
      defaultActiveKey={Object.keys(filters.original)}
    >
      <RadioPanelList
        header={""}
        option={updatedTimeRange[0]}
        key={"Time Range"}
        name={"Time Range"}
        options={originalTimeRange}
        value={updatedTimeRange[0]?.value}
        onChange={onTimeRangeChange}
      />
      <CheckboxPanelList
        header={""}
        key={"Assignee"}
        name={"Assignee"}
        options={originalAssigneeWithTag}
        value={updatedAssignee.map((row) => row.value)}
        onChange={onCheckboxListChange}
        search
      />
    </Collapse>
  );

  return (
    <>
      {/* Desktop Filter Sidebar Content */}
      <div className="common-filter issues-filter-desktop">
        <div className="__filter-header">
          <div className="all-options">
            <div className="filter-title-wrapper">
              <span className="filter-text">Filters</span>
              {activeFiltersCount > 0 && (
                <Badge count={activeFiltersCount} className="filter-badge" />
              )}
            </div>
            <Button
              className="clear-all-btn"
              icon={cancelIcon}
              type="link"
              danger
              onClick={onClearAll}
            >
              Clear All
            </Button>
          </div>
          <div className="__filter-switch">
            <ListTypeSwitch />
          </div>
        </div>
        {filterContent}
      </div>

      {/* Mobile Floating Action Button */}
      {isMobile && (
        <div className="mobile-filter-trigger">
          <Button
            type="primary"
            icon={<FilterOutlined />}
            onClick={handleOpenDrawer}
            className="filter-fab-btn"
          >
            <span className="fab-text">Filters</span>
            {activeFiltersCount > 0 && (
              <span className="fab-badge">{activeFiltersCount}</span>
            )}
          </Button>
        </div>
      )}

      {/* Mobile Filter Drawer */}
      <Drawer
        title={
          <div className="drawer-title-container">
            <div className="drawer-title-wrapper">
              <span className="drawer-title-text">Filters</span>
              {activeFiltersCount > 0 && (
                <Badge count={activeFiltersCount} className="title-badge" />
              )}
            </div>
            <div className="drawer-switch-wrapper">
              <ListTypeSwitch />
            </div>
          </div>
        }
        placement="right"
        onClose={handleCloseDrawer}
        visible={mobileDrawerOpen}
        className="issues-filter-drawer"
        width={isMobile ? "85%" : 400}
        bodyStyle={{ padding: 0 }}
        footer={
          <div className="drawer-footer-container">
            <div className="drawer-footer">
              <Button onClick={onClearAll} className="clear-btn">
                Clear All
              </Button>
              <Button
                type="primary"
                onClick={handleApplyFilters}
                icon={<CheckOutlined />}
                className="apply-btn"
              >
                Apply Filters
              </Button>
            </div>
          </div>
        }
      >
        <div className="drawer-filter-content">{filterContent}</div>
      </Drawer>
    </>
  );
}